import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Auth as SupabaseAuth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { supabase } from '@/integrations/supabase/client';
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AuthError } from '@supabase/supabase-js';

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDevelopment = process.env.NODE_ENV === 'development';
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEmailSent, setIsEmailSent] = useState(false);

  const mode = searchParams.get('mode') || 'signin';
  const planId = searchParams.get('plan');

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session check error:', sessionError);
        setError(sessionError.message);
        return;
      }
      
      if (session) {
        navigate('/dashboard');
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session);
      
      if (event === 'SIGNED_IN' && session) {
        navigate('/dashboard');
      } else if (event === 'SIGNED_OUT') {
        setError(null);
      } else if (event === 'USER_UPDATED') {
        setIsEmailSent(false);
        toast.success('Email confirmed successfully!');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Welcome to Kanflows</h1>
          <p className="text-muted-foreground">
            {mode === 'signup' ? 'Create an account to continue' : 'Sign in to your account'}
          </p>
        </div>
        
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {isEmailSent && (
          <Alert>
            <AlertDescription>
              A confirmation email has been sent. Please check your inbox and spam folder.
              Once confirmed, you can sign in.
            </AlertDescription>
          </Alert>
        )}
        
        {isLoading ? (
          <div className="text-center">
            <p className="text-muted-foreground">Setting up demo account...</p>
          </div>
        ) : (
          <SupabaseAuth 
            supabaseClient={supabase}
            appearance={{ 
              theme: ThemeSupa,
              variables: {
                default: {
                  colors: {
                    brand: '#000000',
                    brandAccent: '#333333',
                  }
                }
              }
            }}
            view={mode === 'signup' ? 'sign_up' : 'sign_in'}
            providers={['github']}
            redirectTo={`${window.location.origin}/auth/callback`}
            queryParams={{
              plan_id: planId || undefined
            }}
          />
        )}
        
        {isDevelopment && (
          <div className="mt-4 text-center text-sm text-muted-foreground">
            <p>For development, you might want to disable email confirmation in the Supabase dashboard.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Auth;